import React from 'react';
import styled from 'styled-components';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Card,
  Grid,
  Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PaymentIcon from '@mui/icons-material/Payment';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';

/* === Estilos === */

const SidebarContainer = styled(Box)`
  position: fixed;
  top: 64px;
  right: 0;
  width: 350px;
  height: calc(100vh - 64px);
  background-color: #f3f6fb;
  padding: 12px;
  box-shadow: -2px 0 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 900px) {
    display: none;
  }
`;

const CustomerCard = styled(Paper)`
  position: relative;
  padding: 12px;
  border-left: 4px solid #1976d2;
  background-color: #ffffff;
  margin-bottom: 10px;
`;

const EditButton = styled(IconButton)`
  position: absolute !important;
  top: 6px;
  right: 6px;
`;

const TotalsCard = styled(Card)`
  background: linear-gradient(180deg, #2b7cd3 0%, #1976d2 100%);
  color: #ffffff;
  padding: 14px 16px;
  text-align: center;
  margin-bottom: 10px;
  box-shadow: 0 4px 14px rgba(25, 118, 210, 0.22);
  border-radius: 12px;

  & > * {
    margin: 0;
  }
`;

const DocSection = styled(Box)`
  background-color: #ffffff;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #d1d9e6;
  margin-bottom: 10px;
`;

const PayButton = styled(Button)`
  width: 100%;
  padding: 10px 0;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 6px;
`;

const clampSx = (lines = 2) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lines,
  wordBreak: 'break-word',
  lineHeight: 1.3
});

/* === Componente === */

const PosRightSidebar = ({
  customer,
  onOpenCustomerModal,
  totalPayable = 0,
  totalTax = 0,
  documentType,
  docOptions,
  onChangeDocumentType,
  onOpenPaymentModal,
  itemsCount
}) => {
  const getCustomerName = (cust) => {
    if (!cust) return '';
    if (cust.razon_social) return cust.razon_social;
    if (cust.nombres && cust.apellidos) return `${cust.nombres} ${cust.apellidos}`;
    return cust.nombre_mostrar || cust.fullname || cust.name || '';
  };

  const getTipoPersonaLabel = (cust) => {
    if (!cust) return 'Sin información';

    if (cust.rut === '66.666.666-6' || cust.rut === '66666666-6') {
      return 'Consumidor final';
    }

    const code = cust.tipo_persona != null ? Number(cust.tipo_persona) : null;
    const hasGiro = Boolean(cust.tiene_giro);

    if (code === 1) {
      return hasGiro ? 'Persona natural con giro' : 'Persona natural sin giro';
    }
    if (code === 2) {
      return 'Empresa';
    }

    return 'Sin información';
  };

  const normalizeText = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

  const resolveBillingAddress = (cust) => {
    if (!cust) return null;

    if (cust.direccion_facturacion) {
      return cust.direccion_facturacion;
    }

    const direcciones = Array.isArray(cust.direcciones) ? cust.direcciones : [];
    if (!direcciones.length) return null;

    const byType = (...types) =>
      direcciones.find((dir) => types.includes(normalizeText(dir?.tipo)));

    return (
      byType('facturacion', 'facturación') ||
      byType('principal') ||
      direcciones[0] ||
      null
    );
  };

  const formatAddress = (dir) => {
    if (!dir) return '';

    if (dir.direccion_formateada) {
      return dir.direccion_formateada;
    }

    const calle = String(dir.calle || '').trim();
    const numero = String(dir.numero || '').trim();
    const complemento = String(dir.complemento || '').trim();
    const comuna = String(dir.comuna || '').trim();
    const ciudad = String(dir.ciudad || '').trim();
    const region = String(dir.region || '').trim();
    const pais = String(dir.pais || '').trim();

    const calleNumero = [calle, numero].filter(Boolean).join(' ').trim();
    const linea1 = [calleNumero, complemento].filter(Boolean).join(', ');
    const linea2 = [comuna, ciudad, region, pais].filter(Boolean).join(', ');

    return [linea1, linea2].filter(Boolean).join(' · ');
  };

  const billingAddress = resolveBillingAddress(customer);
  const billingAddressText = formatAddress(billingAddress);

  const numericTotal = Number(totalPayable) || 0;
  const numericTax = Number(totalTax) || 0;

  const formattedTotal = new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numericTotal);

  const formattedTax = new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numericTax);

  /*
   * Boleta y Factura son accesos permanentes del POS.
   *
   * POS.js sigue siendo quien decide si el cliente actual permite emitir el
   * documento. En particular, al solicitar Factura con CLIENTE GENÉRICO,
   * handleChangeDocumentType('factura') abre el selector de cliente y deja la
   * factura pendiente hasta elegir un cliente válido.
   *
   * docOptions puede venir filtrado por cliente; por eso completamos aquí las
   * dos opciones principales sin alterar la validación de negocio.
   */
  const sourceDocOptions = Array.isArray(docOptions) ? docOptions : [];

  const findDocOption = (key) =>
    sourceDocOptions.find((option) => normalizeText(option?.key) === key) ||
    sourceDocOptions.find((option) => normalizeText(option?.label) === key);

  const boletaOption =
    findDocOption('boleta') || {
      key: 'boleta',
      Icon: DescriptionIcon,
      label: 'Boleta'
    };

  const facturaOption =
    findDocOption('factura') || {
      key: 'factura',
      Icon: AssignmentIcon,
      label: 'Factura'
    };

  const extraDocOptions = sourceDocOptions.filter((option) => {
    const key = normalizeText(option?.key);
    const label = normalizeText(option?.label);
    return key !== 'boleta' && key !== 'factura' && label !== 'boleta' && label !== 'factura';
  });

  const visibleDocOptions = [boletaOption, facturaOption, ...extraDocOptions];

  return (
    <SidebarContainer>
      <Box>
        <CustomerCard elevation={2}>
          <EditButton
            size="small"
            onClick={onOpenCustomerModal}
            aria-label="Editar cliente"
          >
            <EditIcon fontSize="small" />
          </EditButton>

          {customer ? (
            <>
              <Typography
                variant="h6"
                color="primary"
                title={getCustomerName(customer)}
                sx={{
                  pr: 4,
                  fontSize: '0.98rem',
                  fontWeight: 600,
                  ...clampSx(2)
                }}
              >
                {getCustomerName(customer)}
              </Typography>

              {!!customer.rut && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.25, mb: 0.5 }}
                >
                  ({customer.rut})
                </Typography>
              )}

              <Box sx={{ mt: 0.5 }}>
                <Typography variant="body2" sx={{ mb: 0.35 }}>
                  <strong>Tipo:</strong> {getTipoPersonaLabel(customer)}
                </Typography>

                {Number(customer.tipo_persona) === 1 &&
                  customer.tiene_giro &&
                  customer.razon_social && (
                    <Typography
                      variant="body2"
                      title={customer.razon_social}
                      sx={{ mb: 0.35 }}
                    >
                      <strong>Razón social:</strong> {customer.razon_social}
                    </Typography>
                  )}

                  {customer.giro && (
                    <Typography
                      variant="body2"
                      title={customer.giro}
                      sx={{
                        mb: 0.35,
                        lineHeight: 1.35,
                        wordBreak: 'break-word'
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        Giro:
                      </Box>{' '}
                      <Box component="span">
                        {customer.giro}
                      </Box>
                    </Typography>
                  )}

                  {billingAddressText && (
                    <Typography
                      variant="body2"
                      title={billingAddressText}
                      sx={{
                        mb: 0,
                        lineHeight: 1.35,
                        wordBreak: 'break-word'
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        Dirección:
                      </Box>{' '}
                      <Box component="span">
                        {billingAddressText}
                      </Box>
                    </Typography>
                )}
              </Box>
            </>
          ) : (
            <Box textAlign="center" sx={{ pt: 1, pb: 1 }}>
              <Typography variant="body2" color="textSecondary">
                No hay cliente seleccionado
              </Typography>
              <Button
                variant="contained"
                size="small"
                sx={{ mt: 1 }}
                onClick={onOpenCustomerModal}
              >
                Seleccionar Cliente
              </Button>
            </Box>
          )}
        </CustomerCard>

        <TotalsCard elevation={0}>
          <Typography
            sx={{
              fontSize: '0.95rem',
              fontWeight: 800,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              opacity: 0.98,
              mb: 0.4
            }}
          >
            Total
          </Typography>

          <Typography
            component="div"
            sx={{
              fontSize: 'clamp(3.2rem, 5vw, 4rem)',
              fontWeight: 900,
              lineHeight: 1,
              letterSpacing: '-0.04em',
              whiteSpace: 'nowrap',
              textShadow: '0 2px 6px rgba(0,0,0,0.22)',
              mb: 0.75
            }}
          >
            {formattedTotal}
          </Typography>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.45)', mb: 0.75 }} />

          <Typography
            sx={{
              fontSize: '1.15rem',
              fontWeight: 700,
              lineHeight: 1.2
            }}
          >
            Impuestos: {formattedTax}
          </Typography>
        </TotalsCard>

        <DocSection>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
            Documento Tributario:
          </Typography>

          <Grid container spacing={0.75}>
            {visibleDocOptions.map(({ key, Icon, label }) => {
              const normalizedKey = normalizeText(key);
              const active = normalizeText(documentType) === normalizedKey;

              const palette =
                normalizedKey === 'boleta'
                  ? {
                      main: '#2563a6',
                      soft: '#f4f8fc',
                      active: '#e8f1fb',
                      border: '#b7cce2',
                      activeBorder: '#5b8fc4',
                      hover: '#edf4fb',
                      caption: 'Consumidor final'
                    }
                  : normalizedKey === 'factura'
                    ? {
                        main: '#475569',
                        soft: '#f8fafc',
                        active: '#eef2f6',
                        border: '#cbd5e1',
                        activeBorder: '#64748b',
                        hover: '#f1f5f9',
                        caption: ''
                      }
                    : {
                        main: '#5f6368',
                        soft: '#fafafa',
                        active: '#f0f1f2',
                        border: '#d5d7da',
                        activeBorder: '#8b9097',
                        hover: '#f3f4f5',
                        caption: ''
                      };

              return (
                <Grid item xs={6} key={key}>
                  <Paper
                    elevation={active ? 1 : 0}
                    onClick={() => !active && onChangeDocumentType(key)}
                    role="button"
                    aria-pressed={active}
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (active) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onChangeDocumentType(key);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 0.8,
                      py: 1,
                      minHeight: 62,
                      cursor: active ? 'default' : 'pointer',
                      userSelect: 'none',
                      border: `2px solid ${active ? palette.activeBorder : palette.border}`,
                      backgroundColor: active ? palette.active : palette.soft,
                      color: palette.main,
                      transition: 'background-color 120ms ease, border-color 120ms ease, transform 80ms ease',
                      '&:hover': active
                        ? {}
                        : {
                            backgroundColor: palette.hover,
                            borderColor: palette.main
                          },
                      '&:active': active ? {} : { transform: 'scale(0.985)' },
                      '&:focus-visible': {
                        outline: `3px solid ${palette.border}`,
                        outlineOffset: 2
                      }
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 24,
                        color: palette.main
                      }}
                    />

                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.2,
                        textAlign: 'center',
                        color: 'inherit',
                        fontWeight: 800,
                        lineHeight: 1.15
                      }}
                    >
                      {label}
                    </Typography>


                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </DocSection>
      </Box>

      <Box>
        <Divider sx={{ mb: 1.25 }} />
        <PayButton
          variant="contained"
          color="success"
          startIcon={<PaymentIcon />}
          onClick={onOpenPaymentModal}
          disabled={itemsCount === 0}
        >
          Pagar (F9)
        </PayButton>
      </Box>
    </SidebarContainer>
  );
};

export default PosRightSidebar;